import React, { useState, useEffect } from "react";
import axios from "axios";
import { useCookies } from "react-cookie";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  uploadStoreMatrix,
  fetchImportedFiles,
} from "../api/ImportStoreMatrixApi";
import { axiosWithRefresh } from "../api/AxiosWithRefresh";
import CustomDialog from "../components/CustomDialog";

const apiBaseUrl = process.env.REACT_APP_API_URL;

const ImportStoreMatrix = () => {
  const [cookies, setCookie, removeCookie] = useCookies(["auth_token"]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importFiles, setImportFiles] = useState([]);
  const [sortField, setSortField] = useState("imported_date");
  const [sortDirection, setSortDirection] = useState("asc");

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogType, setDialogType] = useState("error");

  const [sendToAllocation, setSendToAllocation] = useState(false); // Checkbox state
  const [allocationName, setAllocationName] = useState(""); // Input for allocation search parameters

  useEffect(() => {
    loadImportedFiles();
  }, []);

  const loadImportedFiles = async () => {
    setLoading(true);
    try {
      const data = await fetchImportedFiles();
      setImportFiles(data);
    } catch (error) {
      console.error("Failed to load imported files", error);
    } finally {
      setLoading(false); // End loading
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const handleCheckboxChange = (event) => {
    setSendToAllocation(event.target.checked);
    if (!event.target.checked) setAllocationName(""); // Clear input if unchecked
  };

  const handleNameChange = (event) => {
    setAllocationName(event.target.value);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setDialogMessage("Please select a file to upload.");
      setDialogType("warning");
      setDialogOpen(true);
      return;
    }

    setIsUploading(true);

    try {
      // Step 1: Upload the file
      const response = await uploadStoreMatrix(
        selectedFile,
        sendToAllocation,
        allocationName
      );
      // Handle the response message based on allocation status

      // Step 2: Handle success response and set dialog message
      if (response.message) {
        const successMessage = sendToAllocation
          ? "File imported and sent to allocation successfully!"
          : "File imported successfully!";
        setDialogMessage(response.message || successMessage);
        setDialogType("success");
      }

      // Reload the imported files list
      await loadImportedFiles();
    } catch (error) {
      // Step 3: Handle errors and set dialog message
      console.error("Error during import:", error);

      const errorMessage =
        error.error || "Failed to complete the operation. Please try again.";
      setDialogMessage(errorMessage);
      setDialogType("error");
    } finally {
      setIsUploading(false);
      setDialogOpen(true);
    }
  };

  const handleDownloadTemplate = async (division) => {
    try {
      const response = await axiosWithRefresh({
        method: "GET",
        url: `${apiBaseUrl}/import_store_matrix/download_template`,
        responseType: "arraybuffer",
        params: { division },
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${division}_store_matrix_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading template:", error);
      setDialogMessage("Error downloading template. Please try again.");
      setDialogType("error");
      setDialogOpen(true);
    }
  };

  // Sorting Functionality
  const handleSort = (field) => {
    const isAsc = sortField === field && sortDirection === "asc";
    const direction = isAsc ? "desc" : "asc";
    setSortField(field);
    setSortDirection(direction);

    const sortedData = [...importFiles].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      // If sorting by time fields, combine with the imported_date
      if (field === "import_start_time" || field === "import_end_time") {
        aValue = `${a.imported_date} ${aValue || ""}`.trim();
        bValue = `${b.imported_date} ${bValue || ""}`.trim();
      }

      // Ensure values are converted to Date objects for accurate comparison
      const aDate = new Date(aValue);
      const bDate = new Date(bValue);

      if (aDate < bDate) return direction === "asc" ? -1 : 1;
      if (aDate > bDate) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setImportFiles(sortedData);
  };

  const tableMessage = importFiles.some((file) => file.imported_by)
    ? `Below is the list of all imports done by you and other users:`
    : `Below is the list of last ${importFiles.length} imports done by you:`;
  // Enable Import Button Logic
  const isImportEnabled = () => {
    if (!selectedFile) return false; // File is required
    if (sendToAllocation && allocationName.trim() === "") return false; // Allocation name is required if checkbox is checked
    return true; // Enable if above conditions are met
  };
  return (
    <Box sx={{ padding: 4 }}>
      {/* Custom Dialog */}
      <CustomDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={
          dialogType === "success"
            ? "Success"
            : dialogType === "warning"
            ? "Warning"
            : "Error"
        }
        message={dialogMessage}
        type={dialogType}
        primaryButtonText="OK"
      />

      {/* Header Section */}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h6" sx={{ marginBottom: 1 }}>
          Import Matrix
        </Typography>
        <Box>
          <Button
            onClick={() => handleDownloadTemplate("mens")}
            size="small"
            sx={{ textTransform: "none", color: "primary.main" }}
          >
            Men's division template
          </Button>
          |
          <Button
            onClick={() => handleDownloadTemplate("womens")}
            size="small"
            sx={{ textTransform: "none", color: "primary.main", ml: 1 }}
          >
            Women's division template
          </Button>
        </Box>
      </Box>

      <Divider sx={{ marginY: 2 }} />

      {/* Instructions */}
      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontStyle: "italic",
          fontSize: 14,
          marginBottom: 2,
        }}
      >
        * To send to the Allocation App please select the checkbox. <br />
        * The columns Style and CC (Color Code) are mandatory when importing the
        file. <br />* Put X in the store you want to include.
      </Typography>

      <Divider sx={{ marginY: 2 }} />

      {/* File Upload Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          marginBottom: 2,
        }}
      >
        {/* Label aligned left */}
        <Box sx={{ minWidth: "200px" }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: "normal",
              whiteSpace: "nowrap",
            }}
          >
            Select a file to Import *
          </Typography>
        </Box>

        {/* File Input and Import Button */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4 }}>
          <TextField
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            disabled={isUploading}
            size="small"
            sx={{
              width: "40%",
              minWidth: "300px",
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={sendToAllocation}
                onChange={handleCheckboxChange}
              />
            }
            label="Send to Allocation App"
          />
          {sendToAllocation && (
            <TextField
              label="Allocation Saved Search Name"
              value={allocationName}
              onChange={handleNameChange}
              fullWidth
            />
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            startIcon={<CloudUploadIcon />}
            disabled={!isImportEnabled() || isUploading}
          >
            {isUploading ? "Importing..." : "Import"}
          </Button>
        </Box>
      </Box>

      {/* Table Section */}
      <Typography variant="body1" sx={{ fontWeight: "bold", marginBottom: 2 }}>
        {tableMessage}
      </Typography>

      {/* Table with styling */}
      {loading ? (
        <Typography variant="body2" sx={{ textAlign: "center" }}>
          Loading imported files...
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ border: "1px solid #e0e0e0" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  { field: "file_name", label: "Import File Name" },
                  { field: "imported_date", label: "Imported Date PST" },
                  {
                    field: "import_start_time",
                    label: "Import Start Time PST",
                  },
                  { field: "import_end_time", label: "Import End Time PST" },
                  ...(importFiles.some((file) => file.imported_by)
                    ? [{ field: "imported_by", label: "Imported By" }]
                    : []),
                  { field: "status", label: "Status" },
                  {
                    field: "is_sent_to_allocation",
                    label: "Sent to Allocation",
                    align: "right",
                  },
                ].map((column) => (
                  <TableCell
                    key={column.field}
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid #e0e0e0",
                      textAlign: column.align,
                    }}
                  >
                    <TableSortLabel
                      active={sortField === column.field}
                      direction={
                        sortField === column.field ? sortDirection : "asc"
                      }
                      onClick={() => handleSort(column.field)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {importFiles.length > 0 ? (
                importFiles.map((file, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ border: "1px solid #e0e0e0" }}>
                      {file.file_name}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #e0e0e0" }}>
                      {file.imported_date}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #e0e0e0" }}>
                      {file.import_start_time}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #e0e0e0" }}>
                      {file.import_end_time}
                    </TableCell>
                    {file.imported_by && (
                      <TableCell sx={{ border: "1px solid #e0e0e0" }}>
                        {file.imported_by}
                      </TableCell>
                    )}
                    <TableCell sx={{ border: "1px solid #e0e0e0" }}>
                      {file.status}
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #e0e0e0", textAlign: "center" }}
                    >
                      {file.is_sent_to_allocation}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{ border: "1px solid #e0e0e0" }}
                  >
                    No data available in table
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ImportStoreMatrix;
